import React from 'react';
import { Autocomplete, ButtonBase } from '@mui/material';
import { Box, styled } from '@mui/system';

import AttachmentIcon from '@mui/icons-material/Attachment';

import { useCardShows } from '../common/card/useCardDensity';
import TagChip from '../TagChip';
import renderTagOption, { tagPopperSlotProps } from '../common/TagOption';
import { TagEditIcon } from '../common/TagEditChip';
import useEscapeToClose from '../common/useEscapeToClose';
import { BORDER, SANS_STACK, TEXT_DARK } from '../common/tokens';
import MoreTagsChip from './MoreTagsChip';
import ItemTagChip from './ItemTagChip';

const TagsRow = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexWrap: 'nowrap',
  gap: 4,
  overflow: 'hidden',
  alignItems: 'center',
});

const TagAutocomplete = styled(Autocomplete)({
  flexGrow: 1,
  minWidth: 0,
  // MUI keeps the input transparent until focus lands, but this one opens
  // focused — the blank first frame is only a flicker.
  '& .MuiAutocomplete-input': { opacity: 1 },
});

// An outlined field sitting among the chips reads as another chip. Green while
// focused, the way the app's other edit fields mark focus.
const TagEditUnderline = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 220,
  borderBottom: `1px solid ${BORDER}`,
  '&:focus-within': { borderBottomColor: theme.palette.success.main },
}));

const tagInputSx = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: '0 2px 2px',
  // The chips beside it, so a name doesn't change size once it becomes one.
  fontSize: '0.85rem',
  letterSpacing: '0.5px',
  fontFamily: SANS_STACK,
  color: TEXT_DARK,
};

/**
 * The footer's left half: the first tag in full, a `+N` counter for the rest,
 * and the inline picker for adding one. Zen mode leaves the tags readable and
 * takes away the `+` that adds one.
 *
 * The composer's state belongs to the card, not here: once the `+` has
 * collapsed the menu is what opens it, and the row renders for it alone.
 */
export default function CardTags({
  tags, onTagOpen, isProposed, canAdd, vocabulary, onAddTag, relatedItems, onItemRead,
  addingTag, onAddingTag,
}) {
  const shows = useCardShows();

  const closeComposer = React.useCallback(() => onAddingTag(false), [onAddingTag]);

  const showTags = shows('tags');
  // The `+` is a control, so it leaves with the actions; the menu offers
  // "Add tag" from there on.
  const canAddTag = canAdd && shows('tagAdd') && shows('actions');
  const zenTookComposer = !shows('tagAdd');

  // Escape closes the composer only while there is one on screen to close.
  useEscapeToClose(addingTag, closeComposer);

  React.useEffect(() => {
    if (zenTookComposer) closeComposer();
  }, [zenTookComposer, closeComposer]);

  if (!showTags && !addingTag) return null;

  return (
    <TagsRow>
      {showTags && tags && tags.length > 0 && (
        <TagChip
          key={tags[0].id}
          tag={tags[0]}
          onOpen={onTagOpen}
          highlight={isProposed && isProposed(tags[0]) ? 'proposed' : undefined}
          style={{ margin: 0 }}
        />
      )}
      {showTags && shows('moreTags') && tags && tags.length > 1 && (
        <MoreTagsChip
          tags={tags.slice(1)}
          onTagOpen={onTagOpen}
          isProposed={isProposed}
        />
      )}
      {canAddTag && !addingTag && (
        <ItemTagChip
          key="add"
          label="+"
          variant="outlined"
          onClick={() => onAddingTag(true)}
        />
      )}
      {addingTag && (
        <TagAutocomplete
          getOptionLabel={(option) => option.name}
          options={vocabulary}
          renderOption={renderTagOption}
          slotProps={tagPopperSlotProps}
          onKeyDown={(e) => { if (e.key === 'Escape') closeComposer(); }}
          renderInput={({ InputProps, inputProps }) => (
            <TagEditUnderline ref={InputProps.ref}>
              <TagEditIcon />
              <Box component="input" {...inputProps} autoFocus sx={tagInputSx} />
            </TagEditUnderline>
          )}
          onChange={(event, newValue) => {
            onAddTag(newValue);
            closeComposer();
          }}
        />
      )}
      {shows('links') && relatedItems && relatedItems.length > 0 && (
        <ButtonBase
          key="related"
          onClick={() => onItemRead(relatedItems[0])}
          sx={{ flexShrink: 0 }}
        >
          <AttachmentIcon sx={{ fontSize: 18 }} />
        </ButtonBase>
      )}
    </TagsRow>
  );
}
